import React from "react";
import { Gauge, Receipt, MessageSquare, Compass, Sparkles, X, ArrowRight, ArrowLeft, PlayCircle } from "lucide-react";

export const TOUR_STEPS = [
  {
    view: "telesoin-quota", icon: Gauge, title: "Quota télésoin & contrats",
    desc: "Saisissez un CA N-1 et regardez la plateforme calculer le plafond des 20 %, répartir les actes sur l'année et bloquer le 4ᵉ acte d'un même patient. Les contrats officiels (Ordre, salariés) se génèrent en 1 clic.",
  },
  {
    view: "facturation-pro", icon: Receipt, title: "Facturation électronique Factur-X",
    desc: "Émettez une facture test : la plateforme distingue automatiquement acte exonéré de TVA (art. 261-4 CGI) et acte hors nomenclature, puis la transmet au cabinet comptable. Prêt pour l'échéance de septembre 2026.",
  },
  {
    view: "messagerie-instant", icon: MessageSquare, title: "Messagerie instantanée d'équipe",
    desc: "Coordonnez secrétaires, confrères et remplaçants en temps réel — accusés de lecture inclus. Les documents médicaux, eux, restent sur vos boîtes MSSanté réglementaires.",
  },
  {
    view: "reseau", icon: Compass, title: "Réseau de proximité",
    desc: "Cherchez un confrère dans l'Annuaire Santé national (API FHIR ANS) directement sur la carte de votre territoire, et constituez votre réseau d'adressage.",
  },
];

const MODULE_SHORTCUTS = TOUR_STEPS.map(({ view, icon, title }) => ({ view, icon, title }));

export function SandboxWelcome({ brand, onStartTour, onGoModule, onClose }) {
  return (
    <div className="fixed inset-0 z-[120] grid place-items-center bg-slate-900/60 backdrop-blur-[2px] p-4"
         onClick={onClose} data-testid="sandbox-welcome">
      <div className="w-full max-w-2xl rounded-2xl bg-white p-7 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-4">
          <div>
            <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.2em] font-bold" style={{ color: "var(--bp)" }}>
              <Sparkles size={13} /> Bienvenue dans votre sandbox
            </div>
            <h2 className="mt-2 font-display text-slate-900 text-[22px] font-semibold leading-snug">
              Que voulez-vous explorer sur {brand?.name || "votre plateforme"} ?
            </h2>
          </div>
          <button onClick={onClose} aria-label="Fermer" data-testid="sandbox-welcome-close"
                  className="grid place-items-center h-8 w-8 rounded-full text-slate-400 hover:bg-slate-100 shrink-0">
            <X size={16} />
          </button>
        </div>

        <button onClick={onStartTour} data-testid="sandbox-welcome-tour"
                className="mt-5 w-full flex items-center gap-3 rounded-xl p-4 text-left text-white transition hover:brightness-110"
                style={{ background: "var(--bp)" }}>
          <PlayCircle size={22} className="shrink-0" />
          <span className="flex-1">
            <span className="block text-[14.5px] font-semibold">Visite guidée (2 min)</span>
            <span className="block text-[12px] opacity-80">Les 4 modules qui font la différence, présentés un par un</span>
          </span>
          <ArrowRight size={16} />
        </button>

        <div className="mt-4 grid grid-cols-2 gap-2.5">
          {MODULE_SHORTCUTS.map((m) => (
            <button key={m.view} onClick={() => onGoModule(m.view)} data-testid={`sandbox-welcome-${m.view}`}
                    className="flex items-center gap-2.5 rounded-xl border border-slate-200 bg-white p-3.5 text-left hover:border-sky-400 transition">
              <m.icon size={17} style={{ color: "var(--bp)" }} className="shrink-0" />
              <span className="text-[13px] font-medium text-slate-800">{m.title}</span>
            </button>
          ))}
        </div>

        <button onClick={onClose} data-testid="sandbox-welcome-free"
                className="mt-4 w-full text-center text-[12.5px] text-slate-500 hover:text-slate-800">
          Explorer librement →
        </button>
      </div>
    </div>
  );
}

export function SandboxTourCard({ idx, onNext, onPrev, onEnd }) {
  const step = TOUR_STEPS[idx];
  const Icon = step.icon;
  return (
    <div className="fixed bottom-5 right-5 z-[120] w-[340px] max-w-[calc(100vw-40px)] rounded-2xl bg-white shadow-2xl border border-slate-200 p-5"
         data-testid="sandbox-tour-card">
      <div className="flex items-center justify-between">
        <span className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.16em] font-bold" style={{ color: "var(--bp)" }}>
          <Sparkles size={11} /> Visite guidée · {idx + 1}/{TOUR_STEPS.length}
        </span>
        <button onClick={onEnd} aria-label="Terminer" data-testid="sandbox-tour-close"
                className="grid place-items-center h-6 w-6 rounded-full text-slate-400 hover:bg-slate-100">
          <X size={13} />
        </button>
      </div>
      <div className="mt-3 flex items-center gap-2.5">
        <span className="grid place-items-center h-9 w-9 rounded-lg shrink-0" style={{ background: "rgba(15,23,42,0.06)" }}>
          <Icon size={17} style={{ color: "var(--bp)" }} />
        </span>
        <div className="text-[14.5px] font-semibold text-slate-900">{step.title}</div>
      </div>
      <p className="mt-2.5 text-[12.5px] text-slate-600 leading-relaxed">{step.desc}</p>
      <div className="mt-2 flex gap-1">
        {TOUR_STEPS.map((_, i) => (
          <span key={i} className="h-1 flex-1 rounded-full" style={{ background: i <= idx ? "var(--bp)" : "#E2E8F0" }} />
        ))}
      </div>
      <div className="mt-4 flex items-center justify-between">
        <button onClick={onPrev} disabled={idx === 0} data-testid="sandbox-tour-prev"
                className="inline-flex items-center gap-1 text-[12.5px] text-slate-500 hover:text-slate-900 disabled:opacity-30">
          <ArrowLeft size={12} /> Précédent
        </button>
        {idx < TOUR_STEPS.length - 1 ? (
          <button onClick={onNext} data-testid="sandbox-tour-next"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-white text-[12.5px] font-medium hover:brightness-110"
                  style={{ background: "var(--bp)" }}>
            Suivant <ArrowRight size={13} />
          </button>
        ) : (
          <button onClick={onEnd} data-testid="sandbox-tour-finish"
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-white text-[12.5px] font-medium hover:brightness-110"
                  style={{ background: "var(--bp)" }}>
            Terminer la visite
          </button>
        )}
      </div>
    </div>
  );
}
